import React from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { usePaymentQueries } from '../../../hooks/usePaymentQueries';
import { useNotification } from '../../../context/NotificationContext';

const PlanSection: React.FC = () => {
    const { t } = useTranslation();
    const { userPlans, isLoadingUserPlans, userPlansError, createBillingPortalSession } = usePaymentQueries();
    const { showNotification } = useNotification();
    
    const useCreateBillingPortalSession = () => {
        return useMutation({
            mutationFn: createBillingPortalSession,
        });
    };
    
    const billingPortalMutation = useCreateBillingPortalSession();

    const handleBillingPortalClick = async () => {
        try {
            const { url } = await billingPortalMutation.mutateAsync(undefined);
            if (url) {
                window.open(url, '_blank');
            }
        } catch (error: any) {
            console.error('Error opening billing portal:', error);
            // If no customer ID is found, inform the user they need to make a purchase first
            if (error.response?.status === 404) {
                alert(t('settings.plan.errors.noBillingInfo'));
            } else {
                alert(t('settings.plan.errors.billingPortalError', { message: error.message }));
            }
        }
    };

    // Get all user purchases as a single list with proper display names
    const getAllPurchases = (): { key: string; displayName: string; year?: string; transactions?: number; purchaseDate?: string; stripePaymentId?: string; isRefunded?: boolean }[] => {
        if (!userPlans) return [];
        
        const purchases: { key: string; displayName: string; year?: string; transactions?: number; purchaseDate?: string; stripePaymentId?: string; isRefunded?: boolean }[] = [];
        
        // Add tax reports
        const taxReportKeys = Object.keys(userPlans)
            .filter((key) => key.startsWith('taxReports.'))
            .sort((a, b) => b.localeCompare(a)); // Sort in descending order (newest first)
        
        taxReportKeys.forEach(key => {
            const taxYear = key.split('.')[1]; // Extract year from key
            const taxReport = userPlans[key];
            
            if (taxReport.status === 'active') {
                const transactionCount = typeof taxReport.transactionLimit === 'number' ? taxReport.transactionLimit : 1000;
                
                // Format purchase date if available
                let purchaseDate = '';
                if (taxReport.purchaseDate) {
                    if (typeof taxReport.purchaseDate === 'string') {
                        const date = new Date(taxReport.purchaseDate);
                        purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    } else if (taxReport.purchaseDate._seconds) {
                        const date = new Date(taxReport.purchaseDate._seconds * 1000);
                        purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    }
                }
                
                purchases.push({
                    key: `taxReport_${taxYear}`,
                    displayName: t(`plansData.packages.${taxReport.package}.short_description`, { year: taxYear, transactions: transactionCount }),
                    year: taxYear,
                    transactions: transactionCount,
                    purchaseDate,
                    stripePaymentId: taxReport.stripePaymentId,
                    isRefunded: taxReport.status === 'refunded'
                });
            }
        });
        
        // Add expert reviews
        const expertReviewKeys = Object.keys(userPlans)
            .filter((key) => key.startsWith('expertReviews.'))
            .sort((a, b) => b.localeCompare(a));
        
        expertReviewKeys.forEach(key => {
            const year = key.split('.')[1];
            const expertReview = userPlans[key];
            
            if (expertReview.status === 'active') {
                // Format purchase date if available
                let purchaseDate = '';
                if (expertReview.purchaseDate) {
                    if (typeof expertReview.purchaseDate === 'string') {
                        const date = new Date(expertReview.purchaseDate);
                        purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    } else if (expertReview.purchaseDate._seconds) {
                        const date = new Date(expertReview.purchaseDate._seconds * 1000);
                        purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    }
                }
                
                purchases.push({
                    key: `expertReview_${year}`,
                    displayName: t('plansData.addons.expertReview.short_description', { year }),
                    year,
                    purchaseDate,
                    stripePaymentId: expertReview.stripePaymentId,
                    isRefunded: expertReview.status === 'refunded'
                });
            }
        });
        
        // Add addons
        const addonKeys = Object.keys(userPlans).filter((key) => key.startsWith('addons.'));
        
        // No translation map needed anymore as we're using plansData
        
        addonKeys.forEach(key => {
            const addonKey = key.split('.')[1];
            const addon = userPlans[key];
            
            if (addon.status === 'active') {
                // Map the addon key to the correct key in plansData
                const addonPlansDataKey = addonKey === 'Daily Sync' ? 'dailySync' :
                                      addonKey === 'Advanced Analytics' ? 'portfolioInsights' :
                                      addonKey === 'Tax Optimization' ? 'taxOptimization' : 
                                      addonKey === 'We Do Everything' ? 'weDoEverything' : addonKey.toLowerCase();
                
                // Get the display name from plansData translations
                
                // Format the validUntil date if available
                let validUntilDate = '';
                if (addon.validUntil) {
                    if (typeof addon.validUntil === 'string') {
                        const date = new Date(addon.validUntil);
                        validUntilDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    } else if (addon.validUntil._seconds) {
                        const date = new Date(addon.validUntil._seconds * 1000);
                        validUntilDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    }
                }
                
                // Format purchase date if available
                let purchaseDate = '';
                if (addon.purchaseDate) {
                    if (typeof addon.purchaseDate === 'string') {
                        const date = new Date(addon.purchaseDate);
                        purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    } else if (addon.purchaseDate._seconds) {
                        const date = new Date(addon.purchaseDate._seconds * 1000);
                        purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                    }
                } else if (addon.stripePaymentId) {
                    // If no purchase date but we have a payment ID, use current date as fallback
                    const date = new Date();
                    purchaseDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
                }
                
                // For Auto-Sync, calculate purchase date from validUntil if needed
                if (addonPlansDataKey === 'dailySync' && !purchaseDate && validUntilDate) {
                    // Auto-Sync is typically purchased for 1 year, so subtract 1 year from validUntil
                    const validUntilTimestamp = typeof addon.validUntil === 'string' 
                        ? new Date(addon.validUntil).getTime()
                        : addon.validUntil._seconds * 1000;
                    
                    const purchaseDateObj = new Date(validUntilTimestamp);
                    purchaseDateObj.setFullYear(purchaseDateObj.getFullYear() - 1);
                    purchaseDate = `${purchaseDateObj.getDate().toString().padStart(2, '0')}.${(purchaseDateObj.getMonth() + 1).toString().padStart(2, '0')}.${purchaseDateObj.getFullYear()}`;
                }
                
                // Add the addon to the purchases list
                purchases.push({
                    key: `addon_${addonKey}`,
                    displayName: t(`plansData.addons.${addonPlansDataKey}.short_description`, { date: validUntilDate }),
                    purchaseDate,
                    stripePaymentId: addon.stripePaymentId,
                    isRefunded: addon.status === 'refunded'
                });
            }
        });
        
        // Sort purchases by date, most recent first
        return purchases.sort((a, b) => {
            // Extract dates for comparison
            const dateA = a.purchaseDate ? new Date(a.purchaseDate.split('.').reverse().join('-')) : new Date(0);
            const dateB = b.purchaseDate ? new Date(b.purchaseDate.split('.').reverse().join('-')) : new Date(0);
            
            // Sort descending (newer dates first)
            return dateB.getTime() - dateA.getTime();
        });
    };

    // Check if user has any purchased items
    const hasPurchasedItems = (): boolean => {
        return getAllPurchases().length > 0;
    };
    
    // Check if user has any refunded items with Stripe payment IDs
    const hasRefundedItems = (): boolean => {
        if (!userPlans) return false;
        
        // Check all object keys in userPlans for refunded items with stripe payment IDs
        return Object.keys(userPlans).some(key => {
            const item = userPlans[key];
            return item && 
                   item.status === 'refunded' && 
                   item.stripePaymentId && 
                   typeof item.stripePaymentId === 'string' && 
                   item.stripePaymentId.trim() !== '';
        });
    };

    return (
        <>
            {isLoadingUserPlans ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        bgcolor: 'background.paper',
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary', 
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            mb: 2
                        }}
                    >
                        {t('settings.plan.title')}
                    </Typography>
                    
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 2,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                </Paper>
            ) : userPlansError ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        bgcolor: 'background.paper',
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary', 
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            mb: 2
                        }}
                    >
                        {t('settings.plan.title')}
                    </Typography>
                    
                    <Typography color="error" sx={{ fontSize: '1rem' }}>{t('settings.errors.loadUserSettingsFailed')}</Typography>
                </Paper>
            ) : hasPurchasedItems() || hasRefundedItems() ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 3,
                            bgcolor: 'background.paper',
                            height: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            mb: 3
                        }}
                    >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary', 
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            mb: 2
                        }}
                    >
                        {t('settings.plan.title')}
                    </Typography>
                    
                    <Stack spacing={2}>
                        <Stack
                            sx={{
                                width: '100%'
                            }}
                        >
                            {getAllPurchases().map((purchase, index, array) => (
                                <React.Fragment key={purchase.key}>
                                    <Box 
                                        sx={{ 
                                            py: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            width: '100%'
                                        }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            minWidth: 0,
                                            flex: 1 
                                        }}>
                                            <Box sx={{ 
                                                mr: 0.5, 
                                                minWidth: 0, 
                                                flex: 1 
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        color="text.primary" 
                                                        sx={{ 
                                                            fontSize: '1rem',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {purchase.displayName}
                                                    </Typography>
                                                    {purchase.isRefunded && (
                                                        <Typography 
                                                            variant="body2" 
                                                            color="error" 
                                                            sx={{ 
                                                                fontSize: '0.8rem',
                                                                fontWeight: 'normal',
                                                                ml: 1
                                                            }}
                                                            component="span"
                                                        >
                                                            ({t('settings.plan.refunded')})
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                            

                                        </Box>
                                        
                                        {purchase.purchaseDate && (
                                            <Box sx={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                textAlign: 'right', 
                                                minWidth: '150px', 
                                                ml: 2, 
                                                flexShrink: 0 
                                            }}>
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary" 
                                                    sx={{ 
                                                        fontSize: '0.9rem',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {t('settings.plan.purchaseDate')}: {purchase.purchaseDate}
                                                </Typography>
                                                
                                                {/* Invoice links removed as requested */}
                                            </Box>
                                        )}
                                    </Box>
                                    {index < array.length - 1 && (
                                        <Box 
                                            sx={{ 
                                                height: '1px', 
                                                bgcolor: 'divider',
                                                opacity: 0.6,
                                                width: '100%'
                                            }} 
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </Stack>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Button 
                                variant="contained" 
                                color="primary" 
                                href="/plans"
                                size="small"
                                sx={{ 
                                    alignSelf: 'flex-start',
                                    height: '40px'
                                }}
                            >
                                {t('settings.plan.buttons.upgrade')}
                            </Button>
                            {import.meta.env.MODE === 'development' && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<ReceiptIcon />}
                                    onClick={handleBillingPortalClick}
                                    disabled={billingPortalMutation.isPending}
                                    sx={{ 
                                        height: '40px'
                                    }}
                                >
                                    {billingPortalMutation.isPending
                                        ? t('common.loading')
                                        : t('settings.plan.buttons.billing')}
                                </Button>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            ) : (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        bgcolor: 'background.paper',
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary', 
                            fontWeight: 'bold',
                            fontSize: '1.25rem',
                            mb: 2
                        }}
                    >
                        {t('settings.plan.title')}
                    </Typography>
                    
                    <Stack spacing={2}>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                            {t('settings.plan.noPurchases')}
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            href="/plans"
                            size="small"
                            sx={{ 
                                alignSelf: 'flex-start',
                                height: '40px'
                            }}
                        >
                            {t('settings.plan.buttons.upgrade')}
                        </Button>
                    </Stack>
                </Paper>
            )}
        </>
    );
};

export default PlanSection;
